import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { cx } from "@/lib/cx";
import { useReveal } from "@/lib/useReveal";

export type RevealProps = HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  /** Stagger position. Delay is `index * 120ms`. */
  index?: number;
  /** `rise` shifts on the Y axis; `fade` blurs in place (used for imagery). */
  variant?: "rise" | "fade";
};

/**
 * The system's single entrance pattern: opacity 0→1, blur 10px→0,
 * translateY(-20px)→0 over 500ms on `--ease-out`.
 *
 * Under `prefers-reduced-motion` the durations collapse to 1ms via the token
 * layer, so this needs no branch of its own.
 */
export function Reveal({ children, index = 0, variant = "rise", className, style, ...rest }: RevealProps) {
  const { ref, shown } = useReveal<HTMLDivElement>();

  const revealStyle: CSSProperties = {
    opacity: shown ? 1 : 0,
    filter: shown ? "blur(0px)" : "blur(var(--reveal-blur))",
    transform: shown ? "none" : variant === "rise" ? "translateY(var(--reveal-shift))" : "none",
    transitionProperty: "opacity, filter, transform",
    transitionDuration: "var(--dur-reveal)",
    transitionTimingFunction: "var(--ease-out)",
    transitionDelay: `calc(${index} * var(--stagger-step))`,
    ...style,
  };

  return (
    <div ref={ref} className={cx(className)} style={revealStyle} {...rest}>
      {children}
    </div>
  );
}
